// Einheiten und Vermietung (Screen eigentuemer/02): je Einheit Lage, Mieter, Kennzahlen und Hinweis.
// Den Vermietungsstand gibt es nur, wo wir für den Eigentümer vermieten (ganzes Objekt oder SEV).
import { Door, DoorOpen, Info, Key, SignOut, Storefront, WarningCircle } from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { IconChip, LeerZustand, StatusChip, type PortalFarbe, type StatusTon } from '../../components/portal/bausteine';
import type { EinheitSicht, EinheitStatus, ObjektSicht } from '../../lib/portal/eigentuemer';

export const EINHEIT_STIL: Record<EinheitStatus, { label: string; farbe: PortalFarbe; ton: StatusTon; icon: Icon; tip: string }> = {
  vermietet: { label: 'Vermietet', farbe: 'konto', ton: 'ok', icon: Door, tip: 'Die Einheit ist vermietet' },
  leer: { label: 'Leerstand', farbe: 'orange', ton: 'warn', icon: DoorOpen, tip: 'Zurzeit kein laufendes Mietverhältnis' },
  gekuendigt: { label: 'Gekündigt', farbe: 'heizung', ton: 'fehler', icon: SignOut, tip: 'Das Mietverhältnis ist gekündigt' },
  gewerbe: { label: 'Gewerbe', farbe: 'schluessel', ton: 'info', icon: Storefront, tip: 'An ein Gewerbe vermietet' },
  ohne: { label: '', farbe: 'wohnung', ton: 'neutral', icon: Door, tip: '' },
};

const initialen = (name: string) => name.split(/\s+/).filter((w) => /^\p{L}/u.test(w)).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

function EinheitKarte({ e }: { e: EinheitSicht }) {
  const stil = EINHEIT_STIL[e.status];
  const leer = e.status === 'leer';
  const warn = e.status === 'gekuendigt' || leer;
  return (
    <section className={`p-glas p-hebt p-farbe-${stil.farbe}`} style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
      <div className="p-glas-schimmer" />
      <div className="p-glas-rand" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <IconChip farbe={stil.farbe} icon={stil.icon} groesse={40} />
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <span className="p-kuerzen" style={{ fontSize: 17, fontWeight: 800 }}>{e.bezeichnung}</span>
          {e.lage && <span className="p-kuerzen" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--p-w50)' }}>{e.lage}</span>}
        </span>
        {stil.label && <span data-tip={stil.tip}><StatusChip ton={stil.ton}>{stil.label}</StatusChip></span>}
      </div>

      {(e.mieterName || leer) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 16, background: 'var(--p-w05)', minWidth: 0 }}>
          <span aria-hidden style={{
            width: 36, height: 36, borderRadius: 12, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, fontWeight: 800,
            background: leer ? 'var(--p-ton-warn-bg)' : 'linear-gradient(160deg, var(--p-w20), var(--p-w08))', color: leer ? 'var(--p-ton-warn-fg)' : 'var(--p-w)',
          }}>
            {leer ? <Key size={17} weight="fill" /> : initialen(e.mieterName ?? '')}
          </span>
          <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <span className="p-kuerzen" style={{ fontSize: 14, fontWeight: 800 }}>{leer ? 'Leerstand' : e.mieterName}</span>
            {e.mieterSeit && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-w50)' }}>{e.mieterSeit}</span>}
          </span>
        </div>
      )}

      {e.fakten.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${e.fakten.length}, minmax(0, 1fr))`, gap: 8 }}>
          {e.fakten.map(([l, v]) => (
            <div key={l} style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '10px 12px', borderRadius: 14, background: 'var(--p-w05)', minWidth: 0 }}>
              <span className="p-kuerzen" style={{ fontSize: 11, fontWeight: 700, color: 'var(--p-w45)' }}>{l}</span>
              <span className="p-zahl p-kuerzen" style={{ fontSize: 16, fontWeight: 800 }}>{v}</span>
            </div>
          ))}
        </div>
      )}

      {e.hinweis && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 14, fontSize: 12.5, fontWeight: 600, lineHeight: 1.45,
          background: warn ? 'var(--p-ton-fehler-bg)' : 'var(--p-w05)', color: warn ? 'var(--p-notfall-text)' : 'var(--p-w75)',
        }}>
          {warn ? <WarningCircle size={15} weight="fill" style={{ marginTop: 1, flexShrink: 0 }} /> : <Info size={15} weight="fill" style={{ marginTop: 1, flexShrink: 0 }} />}
          {e.hinweis}
        </div>
      )}
    </section>
  );
}

export default function EigentuemerEinheiten({ sicht }: { sicht: ObjektSicht }) {
  if (!sicht.einheiten.length) return <LeerZustand icon={Door} text="Zu diesem Objekt sind keine Einheiten hinterlegt." />;
  return (
    <div className="mp-karten p-rein">
      {sicht.einheiten.map((e) => <EinheitKarte key={e.id} e={e} />)}
    </div>
  );
}
